/**
 * Interview Question Bank editor (Phase 4.2).
 *
 * A read-heavy MUI X DataGrid of the active bank_version's questions with
 * module / dimension filters, plus create + edit + soft-delete via
 * QuestionEditDialog. Row click (or the edit icon) opens the editor; "New
 * question" opens it empty. Edits are in-place (PATCH) within the bank_version —
 * no whole-bank version churn (see routes/interviewBank.js for the rationale).
 *
 * Ported from quill-app/quill-admin's InterviewBankPage.tsx. Redux
 * (interviewSlice) replaced with local useState/useEffect calling
 * quillApi.interview.listQuestions directly. Every mutation happens inside
 * QuestionEditDialog, which gates its own writes with ActionConfirm.
 */

import React from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Chip,
  Stack,
  MenuItem,
  TextField,
  CircularProgress,
  Tooltip,
  IconButton,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { QuestionAnswer as InterviewIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  quillApi,
  InterviewQuestion,
  QuillApiError,
} from '../../../services/quillAdminService';
import { useQuillHealth } from '../QuillHealthContext';
import { HelpTip } from '../../help/HelpTip';
import QuestionEditDialog from './QuestionEditDialog';
import {
  MODULES,
  DIMENSIONS,
  DIMENSION_LABEL,
  humanizeModule,
  SENSITIVITY_COLOR,
} from './interviewConstants';

const InterviewBankPage: React.FC = () => {
  const { status } = useQuillHealth();

  const [questions, setQuestions] = React.useState<InterviewQuestion[]>([]);
  const [bankVersion, setBankVersion] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [moduleFilter, setModuleFilter] = React.useState<string>('');
  const [dimensionFilter, setDimensionFilter] = React.useState<string>('');
  const [includeInactive, setIncludeInactive] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<InterviewQuestion | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await quillApi.interview.listQuestions({
        ...(moduleFilter ? { module: moduleFilter } : {}),
        ...(dimensionFilter ? { dimension_key: dimensionFilter } : {}),
        ...(includeInactive ? { include_inactive: true } : {}),
      });
      setQuestions(res.questions);
      setBankVersion(res.bank_version);
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Failed to load questions.');
    } finally {
      setLoading(false);
    }
  }, [moduleFilter, dimensionFilter, includeInactive]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (q: InterviewQuestion) => {
    setEditing(q);
    setDialogOpen(true);
  };

  const columns: GridColDef<InterviewQuestion>[] = [
    {
      field: 'module',
      headerName: 'Module',
      width: 180,
      valueFormatter: (value) => humanizeModule(String(value)),
    },
    {
      field: 'question_text',
      headerName: 'Question',
      flex: 1,
      minWidth: 260,
      renderCell: (params: GridRenderCellParams<InterviewQuestion, string>) => (
        <Tooltip title={params.value || ''}>
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'dimension_keys',
      headerName: 'Dimensions',
      width: 240,
      sortable: false,
      renderCell: (params: GridRenderCellParams<InterviewQuestion, string[]>) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
          {(params.value || []).map((k) => (
            <Chip key={k} size="small" label={DIMENSION_LABEL[k] || k} />
          ))}
        </Box>
      ),
    },
    {
      field: 'sensitivity',
      headerName: 'Sensitivity',
      width: 150,
      renderCell: (params: GridRenderCellParams<InterviewQuestion, string>) => (
        <Chip
          size="small"
          label={params.value}
          color={SENSITIVITY_COLOR[params.value || 'none'] || 'default'}
          variant={params.value === 'none' ? 'outlined' : 'filled'}
        />
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      type: 'number',
      valueFormatter: (value) => (value == null ? '' : Number(value).toFixed(2)),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 90,
      renderCell: (params: GridRenderCellParams<InterviewQuestion, boolean>) =>
        params.value ? (
          <Chip size="small" label="Active" color="success" variant="outlined" />
        ) : (
          <Chip size="small" label="Off" variant="outlined" />
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<InterviewQuestion>) => (
        <IconButton
          size="small"
          aria-label="edit question"
          onClick={(e) => {
            e.stopPropagation();
            openEdit(params.row);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <InterviewIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Interview Question Bank
        </Typography>
        <HelpTip id="quill-interview" />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create, edit, and retire PWE interview questions by module and dimension.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {bankVersion != null && <Chip label={`bank v${bankVersion}`} color="primary" />}
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
            Refresh
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
            New question
          </Button>
        </Stack>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          label="Module"
          size="small"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All modules</MenuItem>
          {MODULES.map((m) => (
            <MenuItem key={m} value={m}>
              {humanizeModule(m)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Dimension"
          size="small"
          value={dimensionFilter}
          onChange={(e) => setDimensionFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All dimensions</MenuItem>
          {DIMENSIONS.map((d) => (
            <MenuItem key={d.key} value={d.key}>
              {d.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant={includeInactive ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setIncludeInactive((v) => !v)}
        >
          {includeInactive ? 'Showing inactive' : 'Hide inactive'}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 620, width: '100%' }}>
        {loading && questions.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={questions}
            columns={columns}
            getRowId={(r) => r.question_id}
            getRowHeight={() => 'auto'}
            loading={loading}
            onRowClick={(params) => openEdit(params.row as InterviewQuestion)}
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            sx={{ '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 } }}
          />
        )}
      </Box>

      <QuestionEditDialog
        open={dialogOpen}
        question={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={(q) => {
          setToast(`Saved "${q.question_text.slice(0, 40)}${q.question_text.length > 40 ? '…' : ''}"`);
          reload();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Local backend • {status === 'up' ? 'connected' : status}
      </Typography>
    </Box>
  );
};

export default InterviewBankPage;
